import { AI } from '@/assets/images';
import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';
import { useMemo, useState } from 'react';
import Button from '@/components/Button/newButtons';
import type { SandboxPromptSchemaProps } from '@/schemas/sandboxPrompts/schema';
import { Textarea } from '@/components/atoms/Textarea';
import { useToast } from '@/components/atoms/Toast';
import { Select } from '@/components/atoms/Select';
import { Tooltip } from '@/components/atoms/Tooltip';
import { SavePromptModal } from './components/SavePromptModal';
import { useSandboxPromptsListQuery } from './hooks/useSandboxPromptsListQuery';
import { useUpdateSandboxPromptMutation } from './hooks/useUpdateSandboxPromptMutation';
import { getSelected } from './utils/getSelected';
import { useActivateDefaultPromptMutation } from './hooks/useActivateDefaultPromptMutation';

export const AIPromptPage = () => {
	const toast = useToast();

	const [selected, setSelected] = useState('');
	const [selectedUserPrompt, setSelectedUserPrompt] = useState(
		'AI User Prompt Text...'
	);
	const [selectedSystemPrompt, setSelectedSystemPrompt] = useState(
		'AI System Prompt Text...'
	);

	const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
	const [isAppliedPrompt, setIsAppliedPrompt] = useState(false);

	const { data: aiPromptData } = useSandboxPromptsListQuery();
	const { mutate: updateMutation } = useUpdateSandboxPromptMutation({
		onSuccess: data => {
			toast.toast({
				title: `Prompt "${data.data.name}" successfully applied.`,
				color: 'success'
			});
		},
		onError: error => {
			toast.toast({
				title: 'Failed to deactivate the previous active prompt.',
				description: error.problem,
				color: 'error'
			});
		}
	});

	const [editableUserPrompt, setEditableUserPrompt] =
		useState(selectedUserPrompt);
	const [editableSystemPrompt, setEditableSystemPrompt] =
		useState(selectedSystemPrompt);

	const currentOptions =
		aiPromptData?.data?.data?.map(prompt => ({
			...prompt
		})) || [];

	const { mutate: activateDefault } = useActivateDefaultPromptMutation({
		onSuccess: () => {
			toast.toast({
				title: `Default prompt for "${selectedOption?.type}" activated.`,
				color: 'success'
			});
		}
	});

	const allowedPromptTypeParametersMap = {
		'business-search': ['{{query}}'],
		'ai-snippets': [
			'{{business_name}}',
			'{{categories}}',
			'{{about_info}}',
			'{{email_body}}'
		],
		'template-grader': ['{{subject}}', '{{body}}'],
		'ai-sequences': [
			'{{target_audience}}',
			'{{offering}}',
			'{{value_proposition_text}}',
			'{{signature}}'
		]
	};
	const [invalidUserParams, setInvalidUserParams] = useState<string[]>([]);
	const [invalidSystemParams, setInvalidSystemParams] = useState<string[]>(
		[]
	);

	const getInvalidParams = (
		prompt: string,
		promptType: keyof typeof allowedPromptTypeParametersMap
	) => {
		const paramRegex = /{{(.*?)}}/g;
		const foundParams = prompt.match(paramRegex) || [];
		const allowedParams = allowedPromptTypeParametersMap[promptType] || [];
		return foundParams.filter(param => !allowedParams.includes(param));
	};

	const selectedOption = getSelected({
		value: selected,
		optionsArray: currentOptions
	});

	// onChange handler for RichEditor
	const handleEditorChangeUserPrompt = (
		event: React.ChangeEvent<HTMLTextAreaElement>
	) => {
		if (!selectedOption?.type) return;

		const updatedValue = event.target.value;
		setEditableUserPrompt(updatedValue);

		const invalidParams = getInvalidParams(
			updatedValue,
			selectedOption.type
		);
		setInvalidUserParams(invalidParams);
	};

	const handleEditorChangeSystemPrompt = (
		event: React.ChangeEvent<HTMLTextAreaElement>
	) => {
		if (!selectedOption?.type) return;

		const updatedValue = event.target.value;
		setEditableSystemPrompt(updatedValue);

		const invalidParams = getInvalidParams(
			updatedValue,
			selectedOption.type
		);
		setInvalidSystemParams(invalidParams);
	};

	const handleSelect = (value: string) => {
		const selectedOptionBody = getSelected({
			value,
			optionsArray: currentOptions
		});

		const systemPrompt =
			selectedOptionBody?.systemPrompt ?? 'AI System Prompt Text...';

		const userPrompt =
			selectedOptionBody?.userPrompt ?? 'AI User Prompt Text...';

		setSelected(value);
		setEditableUserPrompt(userPrompt);
		setSelectedUserPrompt(userPrompt);
		setEditableSystemPrompt(systemPrompt);
		setSelectedSystemPrompt(systemPrompt);
	};

	const handleApplySelectedPrompt = async (
		selectedOptionApply: SandboxPromptSchemaProps | undefined
	) => {
		if (!selectedOptionApply?.name) {
			toast.toast({
				title: 'No prompt selected to apply.',
				color: 'error'
			});

			return;
		}

		if (selectedOptionApply.isDefault && selectedOptionApply.type) {
			return activateDefault({ type: selectedOptionApply.type });
		}

		updateMutation({
			promptId: selectedOptionApply.id || '',
			data: {
				...selectedOptionApply,
				isActive: true
			}
		});
	};

	return (
		<>
			<div className="flex h-full w-full flex-col gap-4 p-3">
				<HeaderComponent
					icon={<AI />}
					name="AI Prompts"
					description="Create and customize AI prompts for all AI features on our platform. (Admin only feature)"
				/>

				<div className="flex h-full w-full flex-col gap-3 overflow-auto md:flex-row">
					<div className="flex h-full min-w-0 basis-1/2 flex-col gap-2">
						<div className="flex items-end gap-3 md:w-fit md:flex-row md:items-center">
							<Select.Root onValueChange={handleSelect}>
								<Select.Trigger className="w-80">
									<Select.Value placeholder="Select AI Prompt...." />
								</Select.Trigger>

								<Select.Content className="w-80">
									{currentOptions.map(({ id, name }) => (
										<Select.Item
											key={id || name}
											value={id || name}
										>
											<Tooltip.Provider>
												<Tooltip.Root>
													<Tooltip.Trigger>
														{name}
													</Tooltip.Trigger>
													<Tooltip.Content>
														{name}
													</Tooltip.Content>
												</Tooltip.Root>
											</Tooltip.Provider>
										</Select.Item>
									))}
								</Select.Content>
							</Select.Root>

							<Button
								onClick={() =>
									handleApplySelectedPrompt(selectedOption)
								}
							>
								Apply
							</Button>

							<PromptTypeText
								value={selectedOption?.type || ''}
							/>
						</div>

						<Textarea
							key={`SelectedNonEditUserPrompt-${selectedOption?.name || 'none'}`}
							value={selectedUserPrompt}
							readOnly
							className="bg-dm-black2 h-full overflow-auto rounded-md p-2 text-dm-white"
						/>

						<Textarea
							key={`SelectedNonEditSystemPrompt-${selectedOption?.name || 'none'}`}
							value={selectedSystemPrompt}
							readOnly
							className="bg-dm-black2 h-full overflow-auto rounded-md p-2 text-dm-white"
						/>
					</div>

					<div className="flex h-full min-w-0 basis-1/2 flex-col gap-3">
						<div className="flex gap-2">
							<Button
								variant="secondary"
								className="w-full"
								onClick={() => {
									setIsAppliedPrompt(false);
									setIsSaveModalOpen(true);
								}}
								disabled={
									invalidUserParams.length > 0 ||
									invalidSystemParams.length > 0
								}
							>
								Save New Prompt
							</Button>
							<Button
								className="w-full"
								onClick={() => {
									setIsAppliedPrompt(true);
									setIsSaveModalOpen(true);
								}}
								disabled={
									invalidUserParams.length > 0 ||
									invalidSystemParams.length > 0
								}
							>
								Save & Apply New Prompt
							</Button>
						</div>
						<Textarea
							key={`EditableUserPrompt-${selected || 'none'}`}
							value={editableUserPrompt}
							onChange={handleEditorChangeUserPrompt}
							readOnly={false}
							className="h-full overflow-auto rounded-md bg-dm-white p-2 text-dm-black"
						/>
						{invalidUserParams.length > 0 && (
							<p className="mt-1 text-lg text-dm-red">
								Invalid parameters:{' '}
								{invalidUserParams.join(', ')}
							</p>
						)}

						<Textarea
							key={`EditableSystemPrompt-${selected || 'none'}`}
							value={editableSystemPrompt}
							onChange={handleEditorChangeSystemPrompt}
							readOnly={false}
							className="h-full overflow-auto rounded-md bg-dm-white p-2 text-dm-black"
						/>
						{invalidSystemParams.length > 0 && (
							<p className="mt-1 text-lg text-dm-red">
								Invalid parameters:{' '}
								{invalidSystemParams.join(', ')}
							</p>
						)}
					</div>
				</div>
			</div>

			<SavePromptModal
				isOpen={isSaveModalOpen}
				onClose={() => setIsSaveModalOpen(false)}
				userPrompt={editableUserPrompt}
				systemPrompt={editableSystemPrompt}
				isApplied={isAppliedPrompt}
			/>
		</>
	);
};

type PromptTypeTextProps = {
	value: string;
};

const PromptTypeText = ({ value }: PromptTypeTextProps) => {
	const formattedLabel = useMemo(
		() => value?.replace(/-/g, ' '), // Replace hyphens with spaces
		[value]
	);

	return (
		<p className="flex min-w-64 items-end whitespace-nowrap capitalize text-dm-white">
			Prompt Type:
			{formattedLabel}
		</p>
	);
};
